import { useImperativeHandle, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Info, BedDouble, Bath, Ruler, Home } from "lucide-react";
import useSwipePhysics from "@/lib/use_swipe_physics";
import { priceParts } from "@/lib/pricing";
import BadgeVerified from "@/components/badges/badge_verified";
import { ListerBadge } from "@/components/badges/badge_realtor";
import BadgeBoost from "@/components/badges/badge_boost";
import ListerAvatar from "@/components/swipe/lister_avatar";

// Physics live in the shared hook (Tinder-grade: 1:1 tracking, grab-point
// rotation pivot, velocity + distance release, linear velocity-scaled exit).
// `mirrorX` lets the deck grow the card behind; `ref.swipe(action)` lets
// buttons run the same fly-out as a real drag. Tapping the photo opens the
// Tinder-style profile expand via `onExpand(photoIndex)` — it never navigates.
// Depth pose (scale / offset) is owned by the CardStack wrapper; this card
// only ever moves through its own drag/exit physics.
// `edge` = full-bleed mode (the /swipe page): square corners on mobile so the
// card meets the screen edges Tinder style, extra bottom padding to clear the
// overlaid action row. Desktop and every other surface keep the rounded card.
export default function SwipeCard({ listing, onSwipe, onExpand, active = true, mirrorX, onExited, enterFrom = 0, edge = false, ref }) {
  const [photo, setPhoto] = useState(0);
  const pressRef = useRef(null);
  const { likeOpacity, nopeOpacity, triggerSwipe, runPreview, style, dragProps } = useSwipePhysics({
    onSwipe: (action) => onSwipe(action, listing),
    onExited,
    enabled: active,
    mirrorX,
    enterFrom,
  });

  useImperativeHandle(ref, () => ({ swipe: triggerSwipe, preview: runPreview }), [triggerSwipe, runPreview]);

  const photos = listing.photos || [];

  // A real tap (not a drag) on the photo opens the profile expand popup.
  function handlePointerDown(e) {
    pressRef.current = { x: e.clientX, y: e.clientY, t: Date.now() };
  }

  function handlePhotoClick(e) {
    const press = pressRef.current;
    pressRef.current = null;
    if (!press) return;
    const moved = Math.hypot(e.clientX - press.x, e.clientY - press.y);
    if (moved < 8 && Date.now() - press.t < 400) onExpand?.(photo);
  }

  return (
    <motion.div
      className={`absolute inset-0 touch-none select-none overflow-hidden bg-neutral-900 shadow-ambient ${
        edge ? "rounded-none lg:rounded-3xl" : "rounded-3xl"
      } ${active ? "cursor-grab active:cursor-grabbing" : ""}`}
      style={style}
      {...dragProps}
    >
      <div className="absolute inset-0" onPointerDown={handlePointerDown} onClick={handlePhotoClick}>
        {photos.length > 0 ? (
          <img src={photos[photo]} alt={listing.title} draggable={false} loading="lazy" decoding="async" className="h-full w-full object-cover" />
        ) : (
          /* WHY: a listing with zero photos used to render a solid black
             card; show a branded placeholder instead so it stays legible. */
          <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-neutral-900 px-8 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-primary/15 text-brand-primary">
              <Home size={30} strokeWidth={1.7} />
            </span>
            <p className="text-lg font-semibold leading-snug text-white">{listing.title}</p>
            <p className="text-sm text-white/60">Photos coming soon</p>
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-black/90 via-black/55 to-transparent" />
      </div>

      {/* Photo progress dots */}
      <div className="absolute inset-x-0 top-0 flex items-start justify-between p-4">
        <div className="flex gap-1.5 pt-1">
          {photos.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Show photo ${i + 1}`}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); setPhoto(i); }}
              className={`h-1 rounded-full transition-all ${i === photo ? "w-6 bg-white" : "w-3 bg-white/40"}`}
            />
          ))}
        </div>
        <button
          type="button"
          aria-label="View details"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onExpand?.(photo); }}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-white/25 text-white transition hover:bg-white/40"
        >
          <Info size={18} />
        </button>
      </div>

      {/* Drag stamps. WHY the edge offset: in full-bleed mode the /swipe
          page floats its filter pills at right-3 top-14, exactly where the
          NOPE stamp sat — drop both stamps to top-24 on mobile so pills and
          stamps never collide. Desktop (lg) and non-edge surfaces keep the
          classic top-14. */}
      <motion.span
        style={{ opacity: likeOpacity }}
        className={`absolute left-5 -rotate-[18deg] rounded-lg border-4 border-brand-primary bg-white/80 px-4 py-1 text-3xl font-extrabold tracking-[5px] text-brand-primary ${
          edge ? "top-24 lg:top-14" : "top-14"
        }`}
      >
        LIKE
      </motion.span>
      <motion.span
        style={{ opacity: nopeOpacity }}
        className={`absolute right-5 rotate-[18deg] rounded-lg border-4 border-brand-ink bg-white/80 px-4 py-1 text-3xl font-extrabold tracking-[5px] text-brand-ink ${
          edge ? "top-24 lg:top-14" : "top-14"
        }`}
      >
        NOPE
      </motion.span>

      {/* Bottom info. In edge (full-bleed) mode the action row floats ON the
          photo near the bottom, so the text needs more clearance on mobile;
          other surfaces keep the straddling-row clearance. Desktop unchanged. */}
      <div className={`pointer-events-none absolute inset-x-0 bottom-0 p-5 text-white lg:pb-5 ${edge ? "pb-28" : "pb-16"}`}>
        {/* Lister avatar sits just LEFT of the badges (Realtor/Verified),
            above the price. Tap it for the lister's name + a link to all their
            listings. Popover opens upward (avatar sits low on the card). */}
        <div className="pointer-events-auto mb-2 flex flex-wrap items-center gap-2">
          <ListerAvatar listing={listing} above />
          {listing.verified && <BadgeVerified />}
          <ListerBadge type={listing.lister_type} />
          {listing.boosted && <BadgeBoost />}
        </div>
        <p className="text-3xl font-semibold tracking-tight">
          ${priceParts(listing).amount.toLocaleString()}
          <span className="text-base font-normal text-white/80"> {priceParts(listing).unit}</span>
        </p>
        <h3 className="mt-0.5 text-lg font-medium leading-snug">{listing.title}</h3>
        {/* WHY: renters kept asking WHERE the home is — the card names its
            market right on the photo as "City, PROVINCE" (the comma is the
            allowed geographic label form, e.g. "Coquitlam, BC"). */}
        <p className="mt-0.5 text-sm text-white/80">
          {[listing.neighborhood, listing.city, listing.province].filter(Boolean).join(", ")}
          {listing.distance_km != null && <> · {listing.distance_km} km</>}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/25 px-3 py-1 text-xs font-semibold text-white">
            <BedDouble size={13} /> {listing.beds === 0 ? "Studio" : `${listing.beds} bd`}{listing.dens ? ` + ${listing.dens} den` : ""}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/25 px-3 py-1 text-xs font-semibold text-white">
            <Bath size={13} /> {listing.baths} ba
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/25 px-3 py-1 text-xs font-semibold text-white">
            <Ruler size={13} /> {listing.sqft} sqft
          </span>
        </div>
      </div>
    </motion.div>
  );
}
